import { gql } from 'graphql-request';
import { Customer, CustomerStats } from 'src/shared/types';
import { isGraphApiError } from 'src/shared/state/api-slices/graphApiError';

export const createCustomerMutation = gql`
    mutation admin($customerId: String!, $customerName: String!) {
        admin {
            createCustomer(customerId: $customerId, customerName: $customerName) {
                customerId
                customerName
                customerCode
            }
        }
    }
`;

export type CreateCustomerParams = {
    customerId: string;
    customerName: string;
};

export type CreateCustomerResult = {
    admin: {
        createCustomer: Pick<Customer, 'customerId' | 'customerName' | 'customerCode'>;
    };
};

export const isDuplicateCustomerIdError = (error: unknown): boolean =>
    isGraphApiError(error) && (error.failures?.some(failure => failure.code === 'DUPLICATE_CUSTOMER_ID') ?? false);

export const updateCustomerMutation = gql`
    mutation admin($customerId: String!, $customerName: String!) {
        admin {
            updateCustomer(customerId: $customerId, customerName: $customerName) {
                customerId
                customerName
            }
        }
    }
`;

export type UpdateCustomerParams = {
    customerId: string;
    customerName: string;
};

export type UpdateCustomerResult = {
    admin: {
        updateCustomer: Pick<Customer, 'customerId' | 'customerName'>;
    };
};

export const deleteCustomerMutation = gql`
    mutation admin($customerId: String!) {
        admin {
            deleteCustomer(customerId: $customerId) {
                customerId
                customerName
                projectCount
                sampleCount
                assetCount
                chartCount
                portfolioCount
                projectDefinitionDocsRemoved
                projectSitesDocsRemoved
                hlcaChartsDocsRemoved
            }
        }
    }
`;

export type DeleteCustomerParams = {
    customerId: string;
};

/** What the cascade actually removed, summed across every project the organisation owned. */
export type DeleteCustomerResult = {
    admin: {
        deleteCustomer: {
            customerId: string;
            customerName: string;
            projectCount: number;
            sampleCount: number;
            assetCount: number;
            chartCount: number;
            portfolioCount: number;
            projectDefinitionDocsRemoved: number;
            projectSitesDocsRemoved: number;
            hlcaChartsDocsRemoved: number;
        };
    };
};

export const customerStatsQuery = gql`
    query customer($customerId: String!) {
        customer(customerId: $customerId) {
            customerId
            stats {
                projectCount
                sampleCount
                portfolioCount
                projectsBySubscription {
                    subscription
                    count
                }
                users {
                    total
                    internal
                    external
                }
                generatedAt
            }
        }
    }
`;

export type CustomerStatsParams = {
    customerId: string;
};

export type CustomerStatsResult = {
    customer: {
        customerId: string;
        stats: CustomerStats;
    } | null;
};
